import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Copy, Ban, Ticket } from "lucide-react";

const EMPTY = { code: "", percent_off: 10, expires_days: "", max_redemptions: "", email: "" };

function fmtDate(epoch) {
  if (!epoch) return "—";
  return new Date(epoch * 1000).toLocaleDateString("fr-FR");
}

export default function AdminPromo() {
  const [codes, setCodes] = useState([]);
  const [stripeOk, setStripeOk] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const load = () =>
    api.get("/admin/promo-codes").then(r => {
      setCodes(r.data.codes || []);
      setStripeOk(r.data.stripe_configured !== false);
    }).catch(() => toast.error("Erreur de chargement"));

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        code: form.code.trim(),
        percent_off: Number(form.percent_off),
      };
      if (form.expires_days) payload.expires_days = Number(form.expires_days);
      if (form.max_redemptions) payload.max_redemptions = Number(form.max_redemptions);
      if (form.email.trim()) payload.email = form.email.trim();
      const r = await api.post("/admin/promo-codes", payload);
      toast.success(`Code « ${r.data.code} » créé ✿`);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setLoading(false);
    }
  };

  const copy = (code) => {
    navigator.clipboard?.writeText(code);
    toast.success(`« ${code} » copié`);
  };

  const deactivate = async (id, code) => {
    if (!window.confirm(`Désactiver le code « ${code} » ? Il ne sera plus utilisable.`)) return;
    try {
      await api.post(`/admin/promo-codes/${id}/deactivate`);
      toast.success("Code désactivé");
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10">
      <p className="overline text-berulia-rosedeep">BERULIA</p>
      <h1 className="font-heading text-3xl sm:text-4xl mt-2 mb-8">Codes promo</h1>

      {!stripeOk && (
        <div className="mb-6 bg-amber-50 border border-amber-200 text-amber-800 text-sm p-4">
          Stripe n'est pas configuré sur cet environnement — la création de codes est désactivée.
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Création */}
        <form onSubmit={submit} className="lg:col-span-1 bg-white border border-berulia-border p-6 space-y-4 self-start">
          <p className="overline flex items-center gap-2"><Ticket size={14}/> Nouveau code</p>

          <label className="block">
            <span className="overline">Code <span className="text-berulia-inkmuted normal-case tracking-normal">(vide = auto)</span></span>
            <input data-testid="promo-code" value={form.code} onChange={e=>setForm({...form, code:e.target.value})}
              placeholder="PRINTEMPS20" className="mt-1 w-full border border-berulia-border px-3 py-2 uppercase"/>
          </label>

          <label className="block">
            <span className="overline">Remise (%)</span>
            <input data-testid="promo-percent" type="number" min={1} max={100} required value={form.percent_off}
              onChange={e=>setForm({...form, percent_off:e.target.value})}
              className="mt-1 w-full border border-berulia-border px-3 py-2"/>
          </label>

          <label className="block">
            <span className="overline">Validité <span className="text-berulia-inkmuted normal-case tracking-normal">(jours, optionnel)</span></span>
            <input data-testid="promo-days" type="number" min={1} value={form.expires_days}
              onChange={e=>setForm({...form, expires_days:e.target.value})}
              placeholder="Sans expiration" className="mt-1 w-full border border-berulia-border px-3 py-2"/>
          </label>

          <label className="block">
            <span className="overline">Nb d'utilisations max <span className="text-berulia-inkmuted normal-case tracking-normal">(optionnel)</span></span>
            <input data-testid="promo-max" type="number" min={1} value={form.max_redemptions}
              onChange={e=>setForm({...form, max_redemptions:e.target.value})}
              placeholder="Illimité" className="mt-1 w-full border border-berulia-border px-3 py-2"/>
          </label>

          <label className="block">
            <span className="overline">Réservé à un e-mail <span className="text-berulia-inkmuted normal-case tracking-normal">(optionnel)</span></span>
            <input data-testid="promo-email" type="email" value={form.email}
              onChange={e=>setForm({...form, email:e.target.value})}
              placeholder="cliente@exemple.fr" className="mt-1 w-full border border-berulia-border px-3 py-2"/>
            <span className="block mt-1 text-xs text-berulia-inkmuted">Seule cette cliente pourra l'utiliser (au paiement avec ce même e-mail).</span>
          </label>

          <button type="submit" disabled={loading || !stripeOk} data-testid="promo-submit"
            className="w-full bg-berulia-ink text-berulia-beige overline px-6 py-3 disabled:opacity-50">
            {loading ? "Création…" : "Créer le code →"}
          </button>
        </form>

        {/* Liste */}
        <div className="lg:col-span-2 bg-white border border-berulia-border">
          <p className="overline p-4 border-b border-berulia-border">Codes existants — {codes.length}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead className="bg-berulia-beigewarm">
                <tr>
                  <th className="text-left p-3">Code</th>
                  <th className="text-left p-3">Remise</th>
                  <th className="text-left p-3">Utilisations</th>
                  <th className="text-left p-3">Expire</th>
                  <th className="text-left p-3">E-mail</th>
                  <th className="text-left p-3">Statut</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {codes.map(c => (
                  <tr key={c.id} className="border-t border-berulia-border">
                    <td className="p-3 font-mono">{c.code}</td>
                    <td className="p-3">−{c.percent_off}%</td>
                    <td className="p-3">{c.times_redeemed}{c.max_redemptions ? ` / ${c.max_redemptions}` : ""}</td>
                    <td className="p-3">{fmtDate(c.expires_at)}</td>
                    <td className="p-3">{c.email || "—"}</td>
                    <td className="p-3">
                      <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${c.active ? "bg-green-100 text-green-700" : "bg-berulia-beigewarm text-berulia-inkmuted"}`}>
                        {c.active ? "Actif" : "Inactif"}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-3">
                        <button onClick={()=>copy(c.code)} title="Copier" className="text-berulia-inkmuted hover:text-berulia-ink"><Copy size={15}/></button>
                        {c.active && (
                          <button onClick={()=>deactivate(c.id, c.code)} title="Désactiver" className="text-berulia-inkmuted hover:text-red-600"><Ban size={15}/></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {codes.length === 0 && (
                  <tr><td colSpan={7} className="p-8 text-center text-berulia-inkmuted">Aucun code pour l'instant.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
